import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import {
  createTargetingFormValue,
  serializeTargetingFormValue,
  TargetingSupplyType,
  type PersistedTargeting,
  type TargetingFormValue,
} from '../model';
import { TargetingConsumer } from '../config';
import {
  customTargetingFormSchema,
  liverampTargetingFormSchema,
  normalizeCustomTargeting,
  normalizeLiverampTargeting,
  serializeAdvancedTargeting,
  type CustomTargetingFormValue,
  type LiverampTargetingFormValue,
} from '../advancedTargetingModel';
import {
  supplyAppSelectionSchema,
  supplyDomainSelectionSchema,
  tailoredSupplySelectionSchema,
} from '../../campaigns/supply/model';

export enum TargetingStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum TargetingBuilderEditorTab {
  Targeting = 'targeting',
  Custom = 'custom',
  Liveramp = 'liveramp',
}

const idSchema = z.union([z.string(), z.number()]).transform(String);

const legacyDeviceTypeSchema = z
  .looseObject({
    id: idSchema.optional(),
    deviceTypeId: idSchema.optional(),
    excluded: z.boolean().optional().default(false),
  })
  .transform((deviceType) => ({
    ...deviceType,
    deviceTypeId: deviceType.deviceTypeId ?? deviceType.id ?? '',
  }));

const legacyLocationSchema = z.looseObject({
  continentId: nullableOptional(z.string()),
  countryId: nullableOptional(z.string()),
  countryName: nullableOptional(z.string()),
  excluded: z.boolean().optional().default(false),
  type: z.string().optional().default(''),
  geocodeId: nullableOptional(idSchema),
  regionId: nullableOptional(idSchema),
  subRegionId: nullableOptional(idSchema),
  subdivision1Name: nullableOptional(z.string()),
  subdivision2Name: nullableOptional(z.string()),
  locationName: nullableOptional(z.string()),
  tag: nullableOptional(z.string()),
});

const legacyZipCodeSchema = z.looseObject({
  zipCode: z.string(),
  excluded: z.boolean().optional().default(false),
});

const legacyIabCategorySchema = z.looseObject({
  iabCategory: z.string(),
  excluded: z.boolean().optional().default(false),
});

const contextualTargetingValueSchema = z.looseObject({
  name: z.string(),
  excluded: z.boolean().optional().default(false),
});

const legacyDomainSchema = z.preprocess(
  (value) => (typeof value === 'string' ? { id: value } : value),
  z.looseObject({
    id: z.string(),
    url: z.string().optional(),
    domain: z.string().optional(),
    excluded: z.boolean().optional().default(false),
  }),
);

const customTargetingSchema = z.preprocess(
  (value) => value ?? { groups: [] },
  customTargetingFormSchema,
);
const liverampTargetingSchema = z.preprocess(
  normalizeLiverampTargeting,
  liverampTargetingFormSchema,
);
const namedTargetingEntitySchema = z.looseObject({ id: idSchema, name: z.string() });
const targetingLanguageSchema = z.looseObject({
  id: idSchema,
  name: z.string(),
  code: z.string().optional(),
});

export const targetingBuilderSchema = z.looseObject({
  id: idSchema,
  name: z.string(),
  status: z.enum(TargetingStatus).optional().default(TargetingStatus.Inactive),
  deviceTypes: nullableArray(legacyDeviceTypeSchema),
  platforms: nullableArray(z.unknown()),
  includeBundles: z.preprocess((value) => value ?? true, z.boolean()),
  includeIps: z.preprocess((value) => value ?? true, z.boolean()),
  bundlesCount: z.preprocess((value) => value ?? 0, z.number().int().nonnegative()),
  ipsCount: z.preprocess((value) => value ?? 0, z.number().int().nonnegative()),
  reqWithoutDeviceIfa: nullableOptional(z.boolean()),
  reqWithoutAppStoreUrl: nullableOptional(z.boolean()),
  reqFilterOutInstlNotEq1: nullableOptional(z.boolean()),
  reqWithoutContentChannel: nullableOptional(z.boolean()),
  reqWithoutContentNetwork: nullableOptional(z.boolean()),
  geoLocations: nullableArray(legacyLocationSchema),
  zipCodes: nullableArray(legacyZipCodeSchema),
  iabCategories: nullableArray(legacyIabCategorySchema),
  includedChannels: nullableArray(contextualTargetingValueSchema),
  excludedChannels: nullableArray(contextualTargetingValueSchema),
  includedChannelGroups: nullableArray(contextualTargetingValueSchema),
  excludedChannelGroups: nullableArray(contextualTargetingValueSchema),
  includedContentTitles: nullableArray(contextualTargetingValueSchema),
  excludedContentTitles: nullableArray(contextualTargetingValueSchema),
  includedContentKeywords: nullableArray(contextualTargetingValueSchema),
  excludedContentKeywords: nullableArray(contextualTargetingValueSchema),
  includedContentGenres: nullableArray(contextualTargetingValueSchema),
  excludedContentGenres: nullableArray(contextualTargetingValueSchema),
  includedApps: nullableArray(z.unknown()),
  excludedApps: nullableArray(z.unknown()),
  includedPlacements: nullableArray(z.unknown()),
  excludedPlacements: nullableArray(z.unknown()),
  domains: nullableArray(legacyDomainSchema),
  includedTailoredSupplies: nullableArray(z.unknown()),
  excludedTailoredSupplies: nullableArray(z.unknown()),
  tailoredSupplyExcludedApps: nullableArray(z.unknown()),
  tailoredSupplyExcludedDomains: nullableArray(z.unknown()),
  pmpDealIds: nullableArray(z.unknown()),
  xp1: nullableOptional(z.string()),
  xp2: nullableOptional(z.string()),
  xp3: nullableOptional(z.string()),
  xp4: nullableOptional(z.string()),
  xpExcluded: z.preprocess((value) => value ?? false, z.boolean()),
  targetingLRSegments: z.unknown().optional(),
  viewability: nullableOptional(z.number()),
  ctr: nullableOptional(z.number()),
  supplyCompanies: nullableArray(namedTargetingEntitySchema),
  userLanguages: nullableArray(targetingLanguageSchema),
  customTargeting: customTargetingSchema,
  liverampTargeting: liverampTargetingSchema,
});

const targetingBuildersPagedContractSchema = z.looseObject({
  content: z.array(targetingBuilderSchema).optional().default([]),
  totalElements: z.number().optional().default(0),
  totalPages: z.number().optional().default(0),
  number: z.number().optional().default(0),
  size: z.number().optional().default(0),
});

export const targetingBuildersPageSchema = z
  .union([z.array(targetingBuilderSchema), targetingBuildersPagedContractSchema])
  .transform((response) =>
    Array.isArray(response)
      ? {
          content: response,
          totalElements: response.length,
          totalPages: response.length ? 1 : 0,
          number: 0,
          size: response.length,
        }
      : response,
  );

export const targetingBuilderDeviceTypeSchema = z
  .looseObject({
    id: idSchema.optional(),
    deviceTypeId: idSchema.optional(),
    name: z.string(),
  })
  .transform((deviceType) => ({
    id: deviceType.id ?? deviceType.deviceTypeId ?? '',
    name: deviceType.name,
  }));

export type TargetingBuilder = z.infer<typeof targetingBuilderSchema>;
export type TargetingBuildersPage = z.infer<typeof targetingBuildersPageSchema>;
export type TargetingBuilderDeviceType = z.infer<typeof targetingBuilderDeviceTypeSchema>;

export type TargetingBuilderFilter = {
  searchTerm: string;
  page: number;
  size: number;
  statuses: readonly TargetingStatus[];
};

export type TargetingBuilderFormValue = {
  name: string;
  status: TargetingStatus;
  targeting: TargetingFormValue;
  bundlesCount: number;
  ipsCount: number;
  pendingBundleCsv: File | null;
  pendingIpCsv: File | null;
  customTargeting: CustomTargetingFormValue;
  liverampTargeting: LiverampTargetingFormValue;
};

export const defaultTargetingBuilderFilter: TargetingBuilderFilter = {
  searchTerm: '',
  page: 0,
  size: 10,
  statuses: [TargetingStatus.Active],
};

export function serializeTargetingBuilderFilter(filter: TargetingBuilderFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm.trim() || undefined,
    page: filter.page,
    size: filter.size,
    status: filter.statuses,
  });
}

export function resolveTargetingStatusTone(status: TargetingStatus) {
  if (status === TargetingStatus.Active) return DashboardTableStatusTone.Success;
  if (status === TargetingStatus.Inactive) return DashboardTableStatusTone.Info;
  return DashboardTableStatusTone.Neutral;
}

export function createTargetingBuilderFormValue(
  targeting: TargetingBuilder | null | undefined,
): TargetingBuilderFormValue {
  return {
    name: targeting?.name ?? '',
    status: targeting?.status ?? TargetingStatus.Active,
    targeting: createTargetingFormValue(
      targeting ? toPersistedTargeting(targeting) : null,
      TargetingConsumer.Standalone,
    ),
    bundlesCount: targeting?.bundlesCount ?? 0,
    ipsCount: targeting?.ipsCount ?? 0,
    pendingBundleCsv: null,
    pendingIpCsv: null,
    customTargeting: normalizeCustomTargeting(targeting?.customTargeting),
    liverampTargeting: normalizeLiverampTargeting(targeting?.liverampTargeting),
  };
}

export function serializeTargetingBuilderFormValue(
  form: TargetingBuilderFormValue,
  original?: TargetingBuilder,
) {
  const targeting = serializeTargetingFormValue(form.targeting, TargetingConsumer.Standalone);
  return {
    ...original,
    name: form.name.trim(),
    status: form.status,
    deviceTypes: (targeting.deviceTypes ?? []).map((deviceType) => ({
      id: deviceType.deviceTypeId,
      excluded: deviceType.excluded,
    })),
    platforms: targeting.platforms ?? [],
    includeBundles: targeting.includeBundles,
    includeIps: targeting.includeIps,
    reqWithoutDeviceIfa: targeting.reqWithoutDeviceIfa,
    reqWithoutAppStoreUrl: targeting.reqWithoutAppStoreUrl,
    reqFilterOutInstlNotEq1: targeting.reqFilterOutInstlNotEq1,
    reqWithoutContentChannel: targeting.reqWithoutContentChannel,
    reqWithoutContentNetwork: targeting.reqWithoutContentNetwork,
    geoLocations: targeting.geoLocations ?? [],
    zipCodes: targeting.zipCodes ?? [],
    iabCategories: targeting.iabCategories ?? [],
    includedChannels: targeting.includedChannels ?? [],
    excludedChannels: targeting.excludedChannels ?? [],
    includedChannelGroups: targeting.includedChannelGroups ?? [],
    excludedChannelGroups: targeting.excludedChannelGroups ?? [],
    includedContentTitles: targeting.includedContentTitles ?? [],
    excludedContentTitles: targeting.excludedContentTitles ?? [],
    includedContentKeywords: targeting.includedContentKeywords ?? [],
    excludedContentKeywords: targeting.excludedContentKeywords ?? [],
    includedContentGenres: targeting.includedContentGenres ?? [],
    excludedContentGenres: targeting.excludedContentGenres ?? [],
    includedApps: targeting.includedApps ?? [],
    excludedApps: targeting.excludedApps ?? [],
    includedPlacements: targeting.includedPlacements ?? [],
    excludedPlacements: targeting.excludedPlacements ?? [],
    domains: [
      ...toLegacyDomains(targeting.includedDomains, false),
      ...toLegacyDomains(targeting.excludedDomains, true),
    ],
    includedTailoredSupplies: targeting.includedTailoredSupplies ?? [],
    tailoredSupplyExcludedApps: targeting.tailoredSupplyExcludedApps ?? [],
    tailoredSupplyExcludedDomains: targeting.tailoredSupplyExcludedDomains ?? [],
    pmpDealIds: targeting.pmpDealIds ?? [],
    xp1: targeting.xp1,
    xp2: targeting.xp2,
    xp3: targeting.xp3,
    xp4: targeting.xp4,
    xpExcluded: targeting.xpExcluded,
    targetingLRSegments: targeting.targetingLRSegments,
    viewability: targeting.viewability,
    ctr: targeting.ctr,
    supplyCompanies: targeting.supplyCompanies ?? [],
    userLanguages: targeting.userLanguages ?? [],
    excludedTailoredSupplies: targeting.excludedTailoredSupplies ?? [],
    customTargeting: serializeAdvancedTargeting(form.customTargeting),
    liverampTargeting: serializeAdvancedTargeting(form.liverampTargeting),
  };
}

export function createTargetingBuilderMultipartInit(
  payload: unknown,
  files: { bundles?: File | null | undefined; ips?: File | null | undefined } = {},
): RequestInit {
  const formData = new FormData();
  formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (files.bundles) formData.append('bundlesFile', files.bundles);
  if (files.ips) formData.append('ipsFile', files.ips);
  return { method: 'POST', body: formData };
}

function toPersistedTargeting(targeting: TargetingBuilder): PersistedTargeting {
  const includedDomains = targeting.domains
    .filter((domain) => !domain.excluded)
    .map(toSupplyDomainSelection);
  const excludedDomains = targeting.domains
    .filter((domain) => domain.excluded)
    .map(toSupplyDomainSelection);
  return {
    deviceTypes: targeting.deviceTypes,
    platforms: targeting.platforms as PersistedTargeting['platforms'],
    bundlesCount: targeting.bundlesCount,
    ipsCount: targeting.ipsCount,
    includeBundles: targeting.includeBundles,
    includeIps: targeting.includeIps,
    reqWithoutDeviceIfa: targeting.reqWithoutDeviceIfa,
    reqWithoutAppStoreUrl: targeting.reqWithoutAppStoreUrl,
    reqFilterOutInstlNotEq1: targeting.reqFilterOutInstlNotEq1,
    reqWithoutContentChannel: targeting.reqWithoutContentChannel,
    reqWithoutContentNetwork: targeting.reqWithoutContentNetwork,
    geoLocations: targeting.geoLocations,
    zipCodes: targeting.zipCodes,
    supplyType:
      includedDomains.length || excludedDomains.length
        ? TargetingSupplyType.Domains
        : TargetingSupplyType.Apps,
    includedApps: targeting.includedApps.map(toSupplyAppSelection),
    excludedApps: targeting.excludedApps.map(toSupplyAppSelection),
    includedPlacements: targeting.includedPlacements.map(toTargetingPlacement),
    excludedPlacements: targeting.excludedPlacements.map(toTargetingPlacement),
    includedDomains,
    excludedDomains,
    includedTailoredSupplies: targeting.includedTailoredSupplies.map(toTailoredSupplySelection),
    excludedTailoredSupplies: targeting.excludedTailoredSupplies.map(toTailoredSupplySelection),
    tailoredSupplyExcludedApps: targeting.tailoredSupplyExcludedApps.map(toSupplyAppSelection),
    tailoredSupplyExcludedDomains:
      targeting.tailoredSupplyExcludedDomains.map(toSupplyDomainSelection),
    iabCategories: targeting.iabCategories,
    includedChannels: targeting.includedChannels,
    excludedChannels: targeting.excludedChannels,
    includedChannelGroups: targeting.includedChannelGroups,
    excludedChannelGroups: targeting.excludedChannelGroups,
    includedContentTitles: targeting.includedContentTitles,
    excludedContentTitles: targeting.excludedContentTitles,
    includedContentKeywords: targeting.includedContentKeywords,
    excludedContentKeywords: targeting.excludedContentKeywords,
    includedContentGenres: targeting.includedContentGenres,
    excludedContentGenres: targeting.excludedContentGenres,
    pmpDealIds: targeting.pmpDealIds,
    xp1: targeting.xp1,
    xp2: targeting.xp2,
    xp3: targeting.xp3,
    xp4: targeting.xp4,
    xpExcluded: targeting.xpExcluded,
    targetingLRSegments: targeting.targetingLRSegments,
    viewability: targeting.viewability,
    ctr: targeting.ctr,
    supplyCompanies: targeting.supplyCompanies,
    userLanguages: targeting.userLanguages,
  };
}

function toTargetingPlacement(value: unknown) {
  const record = readEntityRecord(value);
  const id = readEntityId(record);
  return {
    ...record,
    id,
    name: typeof record['name'] === 'string' && record['name'].trim() ? record['name'] : id,
  };
}

function toSupplyAppSelection(value: unknown) {
  const record = readEntityRecord(value);
  const id = readEntityId(record);
  return supplyAppSelectionSchema.parse({
    ...record,
    id,
    name: typeof record['name'] === 'string' && record['name'].trim() ? record['name'] : id,
  });
}

function toSupplyDomainSelection(value: unknown) {
  const record = readEntityRecord(value);
  const id = readEntityId(record);
  const url =
    (typeof record['url'] === 'string' && record['url'].trim()) ||
    (typeof record['domain'] === 'string' && record['domain'].trim()) ||
    id;
  return supplyDomainSelectionSchema.parse({ ...record, id, url });
}

function toTailoredSupplySelection(value: unknown) {
  const record = readEntityRecord(value);
  const id = readEntityId(record);
  return tailoredSupplySelectionSchema.parse({
    ...record,
    id,
    name: typeof record['name'] === 'string' && record['name'].trim() ? record['name'] : id,
  });
}

function readEntityRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') return { id: value };
  if (typeof value !== 'object' || value === null) {
    throw new Error('Targeting supply selection must be an object');
  }
  return value as Record<string, unknown>;
}

function toLegacyDomains(items: unknown[] | undefined, excluded: boolean) {
  return (items ?? []).flatMap((item) => {
    const id = readEntityId(item);
    return id ? [{ id, excluded }] : [];
  });
}

function readEntityId(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value !== 'object' || value === null) return '';
  if (!('id' in value)) return '';
  const id: unknown = value.id;
  return typeof id === 'string' ? id : '';
}
